import React, { useState, useCallback } from 'react';
import { EVENT_TITLE } from '../constants.ts';

interface PasswordPromptProps {
  onAuthenticate: (password: string) => void;
  error: string | null;
  // Add onGoToRegistration to the PasswordPromptProps interface
  onGoToRegistration: () => void; 
}

export const PasswordPrompt: React.FC<PasswordPromptProps> = ({ onAuthenticate, error, onGoToRegistration }) => {
  const [password, setPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate async operation for authentication
    await new Promise(resolve => setTimeout(resolve, 500));
    onAuthenticate(password);
    setIsLoading(false);
  }, [password, onAuthenticate]);

  return (
    <div className="p-4 md:p-8 bg-primary-bg rounded-lg font-montserrat w-full max-w-md mx-auto">
      <h2 className="text-3xl font-playfair font-bold text-center text-accent-gold mb-8">Dashboard Access</h2>
      <p className="text-center text-text-white mb-6">Enter the password to view the "{EVENT_TITLE}" RSVP dashboard.</p>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="dashboard-password" className="block text-sm font-medium text-text-white mb-1">Password</label>
          <input
            type="password"
            id="dashboard-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full px-4 py-2 border border-accent-gold rounded-md shadow-sm focus:ring-accent-gold focus:border-accent-gold sm:text-sm bg-primary-bg text-text-white placeholder-gray-500"
            placeholder="Enter password"
            required
            aria-required="true"
          />
        </div>

        {error && (
          <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline ml-2">{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-primary-bg bg-accent-gold hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-gold transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Submit password to access dashboard"
        >
          {isLoading ? 'Verifying...' : 'Access Dashboard'}
        </button>
      </form>

      <button
        onClick={onGoToRegistration}
        className="mt-6 w-full flex justify-center py-2 px-4 border border-accent-gold rounded-md shadow-sm text-base font-medium text-accent-gold hover:bg-accent-gold hover:text-primary-bg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-gold transition duration-300 ease-in-out"
        aria-label="Go back to registration page"
      >
        Go back to Registration
      </button>
    </div>
  );
};